import { Fragment } from 'react';
import Link from 'next/link';
import key from 'weak-key';
import { css2obj } from '~/utils/utils';

/**
 * AMP Body Parser
 * @category Functional Component
 * @extends Component
 * @summary Parses a JSON encoded body field.
 * @prop {Object} content - The body content.
 * @return {Node} React node containing the parsed body field for AMP pages.
 */

const AmpBodyParser =({ content, pageType, siteSection, keywords }) => {
  const parsed = JSON.parse(content);

  let paragraphCount = 0;
  let wordCount = 0;

  if(!parsed) {
    return false;
  }

  const getAmpShortcode = elem => {
    if (!elem?.elem || elem?.elem?.type !== 'shortcode') {
      return false;
    }

    const shortCodeElement = elem?.elem?.element;
    const shortCodeType = shortCodeElement['shortcode-id'];

    // Image
    if(shortCodeType === 'image') {
      const renditions = shortCodeElement.images[0];

      if(shortCodeElement['view-mode'] === 'animated_gif') {
        return(
          <div key={`elem-${key(shortCodeElement)}`} className='shortcode-animatedgif'>
            <img
              className='img-responsive'
              src={ shortCodeElement.images }
              alt={ shortCodeElement?.alt }
            />
          </div>
        );
      }

      return(
        <figure key={`elem-${key(shortCodeElement)}`} className='shortcode-image'>
          <amp-img
            alt={renditions['1280x720']?.alt || ''}
            src={renditions['1280x720'].url}
            width={renditions['1280x720'].width}
            height={renditions['1280x720'].height}
            layout='responsive'
          />
          { shortCodeElement.caption &&
          <figcaption>{ shortCodeElement.caption }</figcaption>
          }
        </figure>
      );
    }

    // Speedbump
    if(shortCodeType === 'speedbump') {
      return(
        <div className='shortcode-speedbump' key={`elem-${key(shortCodeElement)}`}>
          <Link
            href={ shortCodeElement.url }
          >
            <a
              target={ shortCodeElement['shortcode-new-window'] === 1 ? '_blank' : '_self'}
              rel='noopener noreferrer'
            >
              Related: { shortCodeElement['shortcode-title'] }
            </a>
          </Link>
        </div>
      );
    }

    // Video
    if(shortCodeType === 'video'
    && shortCodeElement?.video?.hls_streaming_url) {
      const string = encodeURIComponent(JSON.stringify({...shortCodeElement.video, keywords}));
      const kw = encodeURIComponent(keywords);

      return(
        <div className='shortcode-video' key={`elem-${key(shortCodeElement)}`}>
          <amp-video-iframe
            layout='responsive'
            width='16'
            height='9'
            src={`${ process.env.NEXT_PUBLIC_EMBED_URL }/video-embed?themeBypass=1&sVid=${ string }&kw=${ kw }&pageType=${ pageType }&siteSection=${ siteSection }&optanonConsent=15`}
            poster={ shortCodeElement?.video?.poster_image || '/img/drewlogo_1920x1080.png'}
          />
        </div>
      );
    }

    // Tweet / Social Embed
    if(shortCodeType === 'social-media') {
      const socialUrl = shortCodeElement['shortcode-url'].trim();
      const platform = shortCodeElement['shortcode-url'].match(/(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]/g);

      // Facebook
      if(platform[0] === 'www.facebook.com' || platform[0] === 'facebook.com') {
        let postType = 'post';
        if (socialUrl.indexOf('/videos/') !== -1) {
          postType = 'video';
        } else if (socialUrl.indexOf('?comment_id=') !== -1) {
          postType = 'comment';
        }

        return (
          <div className="shortcode-facebook" key={`elem-${key(shortCodeElement)}`}>
            <amp-facebook
              width="325"
              height="400"
              layout="responsive"
              data-embed-as={postType}
              data-align-center="true"
              data-href={socialUrl}
            />
          </div>
        );
      }

      // Twitter
      if(platform[0] === 'twitter.com') {
        const tweetId = shortCodeElement['shortcode-url'].match(/^https?:\/\/twitter\.com\/(?:#!\/)?(\w+)\/status(es)?\/(\d+)$/)[3];

        return (
          <Fragment key={`elem-${key(elem)}`}>
            <amp-twitter
              width='375'
              height='472'
              layout='responsive'
              data-tweetid={ tweetId }
            />
          </Fragment>
        );
      }

      // Instagram
      if(platform[0] === 'www.instagram.com' || platform[0] === 'instagram.com') {
        const urlMatch = socialUrl.match(/^http?s:\/\/(www)?\.instagram\.com\/p\/([^/]+)\/?$/);

        if (urlMatch && urlMatch.length >= 3) {
          const shortcode = urlMatch[2];
          return (
            <div className="shortcode-instagram" key={`elem-${key(shortCodeElement)}`}>
              <amp-instagram
                data-shortcode={shortcode}
                width="400"
                height="400"
                layout="responsive"
              />
            </div>
          );
        }
      }

      // TikTok
      if(platform[0] === 'www.tiktok.com' || platform[0] === 'tiktok.com') {
        return (
          <div className="shortcode-tiktok" key={`elem-${key(shortCodeElement)}`}>
            <amp-embedly-card
              data-url={socialUrl}
              layout="responsive"
              width="375"
              height="472"
              data-card-theme="light"
              data-card-controls="0"
            />
          </div>
        )
      }

      // YouTube
      if(platform[0] === 'www.youtube.com' || platform[0] === 'youtube.com'  || platform[0] === 'youtu.be') {
        const isShortLink = socialUrl.match(/^https?:\/\/youtu\.be/) !== null;
        const shortLinkRegex = /^https?:\/\/youtu\.be\/(.+)$/;
        const defaultRegex = /^https?:\/\/(www\.)?youtube\.com\/watch\?v=(.+)$/;
        const videoId = isShortLink ? socialUrl.match(shortLinkRegex)[1] : socialUrl.match(defaultRegex)[2];

        return (
          <div className="shortcode-youtube" key={`elem-${key(shortCodeElement)}`}>
            <amp-youtube
              data-videoid={videoId}
              layout="responsive"
              width="480"
              height="270"
              credentials="omit" // enable privacy-enhanced mode: https://amp.dev/documentation/components/amp-youtube#credentials-(optional)
            />
          </div>
        );
      }
    }

    return false;
  }

  const getTag = (elem) => {
    switch (elem.element.tag) {
      case 'div':
        paragraphCount += 1;
        wordCount += 50;
        return getAmpShortcode({
          elem: elem.element.value
        });
      case 'a':
        const attributes = elem.element.attr.class 
          ? { ...elem.element.attr, className: elem.element.attr.class }
          : { ...elem.element.attr }
        delete attributes.class;
        delete(attributes.delay);

        return (
          <a
            key={key(elem)}
            {...attributes}
            dangerouslySetInnerHTML={{ __html: elem.element.value }}
          />
        );
      case 'br':
      case 'hr':
        return <elem.element.tag key={key(elem)} />
      case 'img':
      case 'meta':
        return <elem.element.tag {...elem.element.attr} key={key(elem)} />
      case 'iframe':
        const css_object = elem?.element?.attr?.style ? css2obj(elem.element.attr.style) : null;
        
        if (css_object) {
          return <elem.element.tag src={elem.element.attr.src} scrolling={elem.element.attr.scrolling} style={css_object} key={key(elem)} />
        }

        return <elem.element.tag src={elem.element.attr.src} scrolling={elem.element.attr.scrolling} key={key(elem)} />
      case 'p':
        paragraphCount += 1;
      default:
        return (
          <elem.element.tag key={key(elem)}>
            {getChildren(elem.element.value)}
          </elem.element.tag>
        );
    }
  }

  const getChildren = (children) => {
    if (children[0]) {
      return children.map((child) => {
        if (child.type === 'text') {
          wordCount += child.element.value.split(' ').length;
          return <span key={key(child)} dangerouslySetInnerHTML={{ __html: child.element.value }} />
        } else {
          return getTag(child);
        }
      });
    }
    
    return getTag(children);
  }


  const getMarkup = () => parsed.map((elem, i) => {
    const output = [];
    output.push(getTag(elem));

    return output;
  });

  return (
    <div className='ctd-body'>
      { getMarkup() }
    </div>
  );
}

export default AmpBodyParser;
